import {
  getNodeByTreePath,
  getTreePathOfNode,
  isDocumentNode,
  isElementNode,
  isTextNode,
  isCommentNode,
  isNonEmptyTextNode,
  traverseNode,
  removeSetOfNodes,
  nodeTypes,
  isOneOfTags,
} from 'anodum';

import getStyleTagForCSSGenerator from '../helpers/get-style-tag-for-css-generator';
import mutation from '../../injections/bindings/mutation';
import ReferenceMap from './reference-map';
import {
  UPDATE_BOTH,
  UPDATE_STATIC_ONLY,
  UPDATE_LIVE_ONLY,
} from '../../constants/update-strategies';

const STATIC_TEXT_TOKEN = 'ws-static-text-node';

class MutationDriver {
  constructor(options) {
    this.defaults = {
      customInjections: [],
      onStaticDocumentConnected() {},
      onLiveDocumentComposed() {},
      onLiveDocumentConnected() {},
      onBeforeRemoveStaticElement() {},
      onBeforeRemoveLiveElement() {},
      onAfterRemoveStaticElement() {},
      onAfterRemoveLiveElement() {},
      onBeforeInsertStaticElement() {},
      onBeforeInsertLiveElement() {},
      onAfterInsertStaticElement() {},
      onAfterInsertLiveElement() {},
      onBeforeDuplicateStaticElement() {},
      onBeforeDuplicateLiveElement() {},
      onAfterDuplicateStaticElement() {},
      onAfterDuplicateLiveElement() {},
      onBeforeReplaceStaticElement() {},
      onBeforeReplaceLiveElement() {},
      onAfterReplaceStaticElement() {},
      onAfterReplaceLiveElement() {},
      onBeforeShiftStaticElement() {},
      onBeforeShiftLiveElement() {},
      onAfterShiftStaticElement() {},
      onAfterShiftLiveElement() {},
      onBeforeSetStaticElementAttribute() {},
      onBeforeSetLiveElementAttribute() {},
      onAfterSetStaticElementAttribute() {},
      onAfterSetLiveElementAttribute() {},
      onBeforeRemoveStaticElementAttribute() {},
      onBeforeRemoveLiveElementAttribute() {},
      onAfterRemoveStaticElementAttribute() {},
      onAfterRemoveLiveElementAttribute() {},
      onBeforeSetInlineStaticElementStyle() {},
      onBeforeSetInlineLiveElementStyle() {},
      onAfterSetInlineStaticElementStyle() {},
      onAfterSetInlineLiveElementStyle() {},
      onBeforeSynchronizeStaticElement() {},
      onBeforeSynchronizeLiveElement() {},
      onAfterSynchronizeStaticElement() {},
      onAfterSynchronizeLiveElement() {},
    };

    this.options = {
      ...this.defaults,
      ...options,
    };

    this.referenceMap = new ReferenceMap(options);
  }

  reset() {
    this.staticDOM = undefined;
    this.liveDOM = undefined;
    this.referenceMap.reset();
  }

  connectStaticDocument(staticDOM) {
    if (!isDocumentNode(staticDOM)) {
      throw new TypeError('staticDOM is not a Document');
    }

    this.staticDOM = staticDOM;
    this.options.onStaticDocumentConnected(staticDOM);
  }

  connectLiveDocument(liveDOM) {
    if (!isDocumentNode(liveDOM)) {
      throw new TypeError('liveDOM is not a Document');
    }

    this.liveDOM = liveDOM;

    this.options.onLiveDocumentConnected(liveDOM);
  }

  getStaticDocument() {
    return this.staticDOM;
  }

  getLiveDocument() {
    return this.liveDOM;
  }

  getSelection() {
    return this.liveDOM.defaultView.getSelection();
  }

  composeLiveDocument() {
    const { staticDOM } = this;

    if (!staticDOM) {
      throw new Error('static document is not connected');
    }

    const liveDOM = this.composeLiveNode(staticDOM);

    this.options.onLiveDocumentComposed(liveDOM);

    this.connectLiveDocument(liveDOM);
  }

  composeLiveNode(staticNode, virtual = false) {
    if (!isDocumentNode(staticNode) && !isElementNode(staticNode)) {
      throw new Error('staticNode is not neither Document nor Element');
    }

    const staticRoot = isDocumentNode(staticNode) ? staticNode : staticNode.ownerDocument;
    const staticRootPath = getTreePathOfNode(staticNode);
    const liveNode = staticNode.cloneNode(true);

    traverseNode(
      liveNode,
      (lNode, path) => {
        switch (lNode.nodeType) {
          case nodeTypes.TEXT_NODE: {
            if (!isNonEmptyTextNode(lNode) || isOneOfTags(lNode.parentNode, ['script', 'style']))
              return;

            mutation.preserve(lNode);
            break;
          }
          case nodeTypes.ELEMENT_NODE: {
            const absolutePath = staticRootPath.concat(path.slice(1));
            const sNode = getNodeByTreePath(staticRoot, absolutePath);
            this.applyCustomInjections(lNode);

            if (virtual) {
              this.referenceMap.addVirtualReference(sNode);
            } else {
              this.referenceMap.addReference(lNode, sNode);
            }
            break;
          }
        }
      },
      true
    );

    return liveNode;
  }

  applyCustomInjections(liveNode) {
    const { customInjections } = this.options;
    return customInjections.forEach((injection) => injection.apply && injection.apply(liveNode));
  }

  removeCustomInjections(liveNode) {
    const { customInjections } = this.options;
    return customInjections.forEach((injection) => injection.remove && injection.remove(liveNode));
  }

  removeAllTextInjections(liveElement) {
    if (!isElementNode(liveElement)) {
      throw new Error('liveElement is not an ElementNode');
    }

    const nodesToRemove = [];
    traverseNode(liveElement, (node) => {
      if (node === liveElement) return;

      if (isElementNode(node) && this.referenceMap.hasReference(node)) {
        this.removeCustomInjections(node);
        this.referenceMap.removeReference(node);
      }

      if (isCommentNode(node) && node.nodeValue === STATIC_TEXT_TOKEN) {
        nodesToRemove.push(node);
      }
    });

    removeSetOfNodes(nodesToRemove);
  }

  isDynamicElement(liveElement) {
    if (!isElementNode(liveElement)) {
      throw new Error('liveElement is not an ElementNode');
    }

    const injections = this.options.customInjections;
    const shouldPreserveNode = injections.some((injection) => {
      if (!injection.shouldPreserveNode) return false;
      return injection.shouldPreserveNode(liveElement);
    });
    const hasReference = this.referenceMap.hasReference(liveElement);

    return !hasReference && !shouldPreserveNode;
  }

  isDynamicText(liveNode) {
    if (!isTextNode(liveNode)) {
      throw new Error('liveNode is not an TextNode');
    }

    if (!isNonEmptyTextNode(liveNode)) return false;

    const injections = this.options.customInjections;
    const sibling = liveNode.previousSibling;
    const hasStaticToken = isCommentNode(sibling) && sibling.nodeValue === STATIC_TEXT_TOKEN;
    const shouldPreserveNode = injections.some((injection) => {
      if (!injection.shouldPreserveNode) return false;
      return injection.shouldPreserveNode(liveNode);
    });

    return !hasStaticToken && !shouldPreserveNode;
  }

  isIgnoredElement(liveElement) {
    if (!isElementNode(liveElement)) {
      throw new Error('liveElement is not an ElementNode');
    }

    const injections = this.options.customInjections;

    const shouldIgnoreNode = injections.some((injection) => {
      if (!injection.shouldIgnoreNode) return false;
      return injection.shouldIgnoreNode(liveElement);
    });

    return shouldIgnoreNode;
  }

  hasDynamicAttributes(liveElement) {
    if (!isElementNode(liveElement)) {
      throw new Error('liveElement is not an ElementNode');
    }

    const injections = this.options.customInjections;
    const staticNode = this.referenceMap.getReference(liveElement);
    const liveAttributes = Array.prototype.slice.call(liveElement.attributes);

    return liveAttributes.some((attribute) => {
      const isEqualToStatic = staticNode.getAttribute(attribute.name) === attribute.value;
      const isReferenceAttribute = attribute.name === this.referenceMap.options.referenceAttribute;
      const shouldPreserveAttribute = injections.some((injection) => {
        if (!injection.shouldPreserveAttribute) return false;
        return injection.shouldPreserveAttribute(attribute, liveElement);
      });

      return !isReferenceAttribute && !isEqualToStatic && !shouldPreserveAttribute;
    });
  }

  hasDynamicContent(liveElement) {
    const testElement = liveElement.cloneNode(true);

    let hasDynamicContent = false;

    traverseNode(testElement, (testNode) => {
      switch (testNode.nodeType) {
        case nodeTypes.ELEMENT_NODE:
          if (
            !this.isIgnoredElement(testNode) &&
            (this.isDynamicElement(testNode) || this.hasDynamicAttributes(testNode))
          ) {
            hasDynamicContent = true;
            return true;
          }
          break;

        case nodeTypes.TEXT_NODE:
          if (this.isDynamicText(testNode)) {
            hasDynamicContent = true;
            return true;
          }
          break;
      }

      return false;
    });

    return hasDynamicContent;
  }

  removeElement(liveElement, updateStrategy = UPDATE_BOTH) {
    if (!this.referenceMap.hasReference(liveElement)) {
      throw new Error('liveElement has no reference');
    }

    switch (updateStrategy) {
      case UPDATE_BOTH: {
        const referenceId = this.referenceMap.getReferenceId(liveElement);
        const staticElement = this.referenceMap.getReference(liveElement);
        this.options.onBeforeRemoveStaticElement(staticElement);
        staticElement.parentNode.removeChild(staticElement);
        this.referenceMap.removeFromMap(referenceId);
        this.options.onAfterRemoveStaticElement(staticElement);

        this.options.onBeforeRemoveLiveElement(liveElement);
        liveElement.parentNode.removeChild(liveElement);
        this.options.onAfterRemoveLiveElement(liveElement);
        break;
      }
      case UPDATE_STATIC_ONLY: {
        const referenceId = this.referenceMap.getReferenceId(liveElement);
        const staticElement = this.referenceMap.getReference(liveElement);
        this.options.onBeforeRemoveStaticElement(staticElement);
        staticElement.parentNode.removeChild(staticElement);
        this.referenceMap.removeFromMap(referenceId);
        this.options.onAfterRemoveStaticElement(staticElement);
        break;
      }
      case UPDATE_LIVE_ONLY: {
        this.options.onBeforeRemoveLiveElement(liveElement);
        liveElement.parentNode.removeChild(liveElement);
        this.options.onAfterRemoveLiveElement(liveElement);
        break;
      }
    }
  }

  insertElement(element, liveRefElement, position, updateStrategy = UPDATE_BOTH) {
    if (!this.referenceMap.hasReference(liveRefElement)) {
      throw new Error('liveRefElement has no reference');
    }

    switch (updateStrategy) {
      case UPDATE_BOTH: {
        const staticRefElement = this.referenceMap.getReference(liveRefElement);
        this.options.onBeforeInsertStaticElement(element);
        staticRefElement.insertAdjacentElement(position, element);
        this.options.onAfterInsertStaticElement(element);

        const liveElement = this.composeLiveNode(element);
        this.options.onBeforeInsertLiveElement(liveElement);
        liveRefElement.insertAdjacentElement(position, liveElement);
        this.options.onAfterInsertLiveElement(liveElement);
        break;
      }
      case UPDATE_STATIC_ONLY: {
        const staticRefElement = this.referenceMap.getReference(liveRefElement);
        this.options.onBeforeInsertStaticElement(element);
        staticRefElement.insertAdjacentElement(position, element);
        this.composeLiveNode(element, true);
        this.options.onAfterInsertStaticElement(element);
        break;
      }
      case UPDATE_LIVE_ONLY: {
        const liveElement = this.composeLiveNode(element);
        this.options.onBeforeInsertLiveElement(liveElement);
        liveRefElement.insertAdjacentElement(position, liveElement);
        this.options.onAfterInsertLiveElement(liveElement);
        break;
      }
    }
  }

  updateAtomicCSS(stylesString, updateStrategy = UPDATE_BOTH) {
    switch (updateStrategy) {
      case UPDATE_BOTH: {
        const { staticDOM, liveDOM } = this;
        const staticStyleElement = getStyleTagForCSSGenerator(staticDOM);

        staticStyleElement.innerHTML = stylesString;
        let liveStyleElement = getStyleTagForCSSGenerator(liveDOM, false);

        if (liveStyleElement) {
          liveStyleElement.innerHTML = stylesString;
        } else {
          liveStyleElement = this.composeLiveNode(staticStyleElement);
          liveDOM.head.appendChild(liveStyleElement);
        }
        break;
      }
      case UPDATE_STATIC_ONLY: {
        const { staticDOM } = this;
        const styleElement = getStyleTagForCSSGenerator(staticDOM);

        styleElement.innerHTML = stylesString;
        break;
      }
      case UPDATE_LIVE_ONLY: {
        const { liveDOM } = this;
        const styleElement = getStyleTagForCSSGenerator(liveDOM);

        styleElement.innerHTML = stylesString;
        break;
      }
    }
  }

  replaceElement(element, targetLiveElement, updateStrategy = UPDATE_BOTH) {
    if (!this.referenceMap.hasReference(targetLiveElement)) {
      throw new Error('targetLiveElement has no reference');
    }

    switch (updateStrategy) {
      case UPDATE_BOTH: {
        const referenceId = this.referenceMap.getReferenceId(targetLiveElement);
        const targetStaticElement = this.referenceMap.getReference(targetLiveElement);
        this.options.onBeforeReplaceStaticElement(targetStaticElement, element);
        targetStaticElement.parentNode.replaceChild(element, targetStaticElement);
        this.referenceMap.removeFromMap(referenceId);
        this.options.onAfterReplaceStaticElement(targetStaticElement, element);

        const liveElement = this.composeLiveNode(element);
        this.options.onBeforeReplaceLiveElement(targetLiveElement, liveElement);
        targetLiveElement.parentNode.replaceChild(liveElement, targetLiveElement);
        this.options.onAfterReplaceLiveElement(targetLiveElement, liveElement);
        break;
      }
      case UPDATE_STATIC_ONLY: {
        const targetStaticElement = this.referenceMap.getReference(targetLiveElement);
        this.options.onBeforeReplaceStaticElement(targetStaticElement, element);
        targetStaticElement.parentNode.replaceChild(element, targetStaticElement);
        this.composeLiveNode(element, true);
        this.options.onAfterReplaceStaticElement(targetStaticElement, element);
        break;
      }
      case UPDATE_LIVE_ONLY: {
        const liveElement = this.composeLiveNode(element);
        this.options.onBeforeReplaceLiveElement(targetLiveElement, liveElement);
        targetLiveElement.parentNode.replaceChild(liveElement, targetLiveElement);
        this.options.onAfterReplaceLiveElement(targetLiveElement, liveElement);
        break;
      }
    }
  }

  shiftElement(liveElement, liveRefElement, position, updateStrategy = UPDATE_BOTH) {
    if (!this.referenceMap.hasReference(liveElement)) {
      throw new Error('liveElement has no reference');
    }

    if (!this.referenceMap.hasReference(liveRefElement)) {
      throw new Error('liveRefElement has no reference');
    }

    switch (updateStrategy) {
      case UPDATE_BOTH: {
        const staticElement = this.referenceMap.getReference(liveElement);
        const staticRefElement = this.referenceMap.getReference(liveRefElement);
        this.options.onBeforeShiftStaticElement(staticElement);
        staticRefElement.insertAdjacentElement(position, staticElement);
        this.options.onAfterShiftStaticElement(staticElement);

        this.options.onBeforeShiftLiveElement(liveElement);
        liveRefElement.insertAdjacentElement(position, liveElement);
        this.options.onAfterShiftLiveElement(liveElement);
        break;
      }
      case UPDATE_STATIC_ONLY: {
        const staticElement = this.referenceMap.getReference(liveElement);
        const staticRefElement = this.referenceMap.getReference(liveRefElement);
        this.options.onBeforeShiftStaticElement(staticElement);
        staticRefElement.insertAdjacentElement(position, staticElement);
        this.options.onAfterShiftStaticElement(staticElement);
        break;
      }
      case UPDATE_LIVE_ONLY: {
        this.options.onBeforeShiftLiveElement(liveElement);
        liveRefElement.insertAdjacentElement(position, liveElement);
        this.options.onAfterShiftLiveElement(liveElement);
        break;
      }
    }
  }

  duplicateElement(liveElement, updateStrategy = UPDATE_BOTH) {
    if (!this.referenceMap.hasReference(liveElement)) {
      throw new Error('liveElement has no reference');
    }

    const staticElement = this.referenceMap.getReference(liveElement);
    const newElement = staticElement.cloneNode(true);

    switch (updateStrategy) {
      case UPDATE_BOTH: {
        this.options.onBeforeDuplicateStaticElement(newElement);
        this.options.onBeforeDuplicateLiveElement(liveElement);
        this.insertElement(newElement, liveElement, 'afterend', updateStrategy);
        this.options.onAfterDuplicateStaticElement(newElement);
        this.options.onAfterDuplicateLiveElement(liveElement);
        break;
      }
      case UPDATE_STATIC_ONLY: {
        this.options.onBeforeDuplicateStaticElement(newElement);
        this.insertElement(newElement, liveElement, 'afterend', updateStrategy);
        this.options.onAfterDuplicateStaticElement(newElement);
        break;
      }
      case UPDATE_LIVE_ONLY: {
        this.options.onBeforeDuplicateLiveElement(liveElement);
        this.insertElement(newElement, liveElement, 'afterend', updateStrategy);
        this.options.onAfterDuplicateLiveElement(liveElement);
        break;
      }
    }
  }

  setElementAttribute(liveElement, attribute, value, updateStrategy = UPDATE_BOTH) {
    if (!this.referenceMap.hasReference(liveElement)) {
      throw new Error('liveElement has no reference');
    }

    switch (updateStrategy) {
      case UPDATE_BOTH: {
        const staticElement = this.referenceMap.getReference(liveElement);
        this.setStaticElementAttribute(staticElement, attribute, value);

        this.options.onBeforeSetLiveElementAttribute(liveElement, attribute, value);
        liveElement.setAttribute(attribute, value);
        this.options.onAfterSetLiveElementAttribute(liveElement, attribute, value);
        break;
      }
      case UPDATE_STATIC_ONLY: {
        const staticElement = this.referenceMap.getReference(liveElement);
        this.setStaticElementAttribute(staticElement, attribute, value);
        break;
      }
      case UPDATE_LIVE_ONLY: {
        this.options.onBeforeSetLiveElementAttribute(liveElement, attribute, value);
        liveElement.setAttribute(attribute, value);
        this.options.onAfterSetLiveElementAttribute(liveElement, attribute, value);
        break;
      }
    }
  }

  setStaticElementAttribute(staticElement, attribute, value) {
    if (!staticElement) {
      throw new Error('staticElement is not defined');
    }

    this.options.onBeforeSetStaticElementAttribute(staticElement, attribute, value);
    staticElement.setAttribute(attribute, value);
    this.options.onAfterSetStaticElementAttribute(staticElement, attribute, value);
  }

  removeElementAttribute(liveElement, attribute, updateStrategy = UPDATE_BOTH) {
    if (!this.referenceMap.hasReference(liveElement)) {
      throw new Error('liveElement has no reference');
    }

    switch (updateStrategy) {
      case UPDATE_BOTH: {
        const staticElement = this.referenceMap.getReference(liveElement);
        this.options.onBeforeRemoveStaticElementAttribute(staticElement, attribute);
        staticElement.removeAttribute(attribute);
        this.options.onAfterRemoveStaticElementAttribute(staticElement, attribute);

        this.options.onBeforeRemoveLiveElementAttribute(liveElement, attribute);
        liveElement.removeAttribute(attribute);
        this.options.onAfterRemoveLiveElementAttribute(liveElement, attribute);
        break;
      }
      case UPDATE_STATIC_ONLY: {
        const staticElement = this.referenceMap.getReference(liveElement);
        this.options.onBeforeRemoveStaticElementAttribute(staticElement, attribute);
        staticElement.removeAttribute(attribute);
        this.options.onAfterRemoveStaticElementAttribute(staticElement, attribute);
        break;
      }
      case UPDATE_LIVE_ONLY: {
        this.options.onBeforeRemoveLiveElementAttribute(liveElement, attribute);
        liveElement.removeAttribute(attribute);
        this.options.onAfterRemoveLiveElementAttribute(liveElement, attribute);
        break;
      }
    }
  }

  hasElementAttribute(liveElement, attribute) {
    if (!this.referenceMap.hasReference(liveElement)) {
      throw new Error('liveElement has no reference');
    }

    const staticElement = this.referenceMap.getReference(liveElement);

    return staticElement.hasAttribute(attribute);
  }

  getElementAttribute(liveElement, attribute) {
    if (!this.referenceMap.hasReference(liveElement)) {
      throw new Error('liveElement has no reference');
    }

    const staticElement = this.referenceMap.getReference(liveElement);

    return staticElement.getAttribute(attribute);
  }

  getInlineElementStyle(liveElement, propertyName) {
    if (!this.referenceMap.hasReference(liveElement)) {
      throw new Error('liveElement has no reference');
    }

    const staticElement = this.referenceMap.getReference(liveElement);

    return staticElement.style.getPropertyValue(propertyName) || null;
  }

  setInlineElementStyle(liveElement, propertyName, value, updateStrategy = UPDATE_BOTH) {
    if (!this.referenceMap.hasReference(liveElement)) {
      throw new Error('liveElement has no reference');
    }

    switch (updateStrategy) {
      case UPDATE_BOTH: {
        const staticElement = this.referenceMap.getReference(liveElement);
        const staticStyles = staticElement.style;
        this.options.onBeforeSetInlineStaticElementStyle(staticElement, propertyName, value);
        staticStyles.setProperty(propertyName, value);
        this.options.onAfterSetInlineStaticElementStyle(staticElement, propertyName, value);

        const liveStyles = liveElement.style;
        this.options.onBeforeSetInlineLiveElementStyle(liveElement, propertyName, value);
        liveStyles.setProperty(propertyName, value);
        this.options.onAfterSetInlineLiveElementStyle(liveElement, propertyName, value);
        break;
      }
      case UPDATE_STATIC_ONLY: {
        const staticElement = this.referenceMap.getReference(liveElement);
        const staticStyles = staticElement.style;
        this.options.onBeforeSetInlineStaticElementStyle(staticElement, propertyName, value);
        staticStyles.setProperty(propertyName, value);
        this.options.onAfterSetInlineStaticElementStyle(staticElement, propertyName, value);
        break;
      }
      case UPDATE_LIVE_ONLY: {
        const liveStyles = liveElement.style;
        this.options.onBeforeSetInlineLiveElementStyle(liveElement, propertyName, value);
        liveStyles.setProperty(propertyName, value);
        this.options.onAfterSetInlineLiveElementStyle(liveElement, propertyName, value);
        break;
      }
    }
  }

  transformToStatic(liveElement, updateReference = false) {
    if (!this.referenceMap.hasReference(liveElement)) {
      throw new Error('liveElement has no reference');
    }

    const refAttribute = this.referenceMap.options.referenceAttribute;
    const transformedElement = this.staticDOM.importNode(liveElement, true);
    const nodesToRemove = [];
    const nodesToPullOut = [];
    const nodesToRevert = [];

    traverseNode(
      transformedElement,
      (tNode, path) => {
        const lNode = getNodeByTreePath(liveElement, path);

        switch (tNode.nodeType) {
          case nodeTypes.ELEMENT_NODE: {
            // Don't remove static element
            if (tNode.hasAttribute(refAttribute)) {
              nodesToRevert.push(tNode);
              return;
            }
            // Preserve required dynamic elements
            if (!this.isDynamicElement(tNode)) {
              this.applyCustomInjections(lNode);
              if (updateReference) {
                this.referenceMap.addReference(lNode, tNode);
              }
              nodesToRevert.push(tNode);
              return;
            }
            // Prepare to pull out inner static elements from a dynamic one
            if (tNode.childNodes.length) {
              nodesToPullOut.push(tNode);
              return;
            }
            // Or just delete an additive node
            nodesToRemove.push(tNode);
            break;
          }
          case nodeTypes.COMMENT_NODE: {
            if (
              tNode.nodeValue === STATIC_TEXT_TOKEN &&
              tNode.nextSibling &&
              isTextNode(tNode.nextSibling)
            )
              return;
            // Remove the STATIC_TEXT_TOKEN if there is no a text node coming after it.
            nodesToRemove.push(tNode);
            break;
          }
          case nodeTypes.TEXT_NODE: {
            const token = tNode.previousSibling;
            // Don't remove static text
            if (isCommentNode(token) && token.nodeValue === STATIC_TEXT_TOKEN) {
              nodesToRemove.push(token);
              return;
            }
            // Preserve required text nodes
            if (!this.isDynamicText(tNode)) {
              const commentNode = document.createComment(STATIC_TEXT_TOKEN);
              lNode.parentNode.insertBefore(commentNode, lNode);
              return;
            }
            // Prepare to delete dynamically added text nodes
            nodesToRemove.push(tNode);
            break;
          }
        }
      },
      true
    );

    // Pull out all content for dynamic nodes
    nodesToPullOut.forEach((node) => {
      while (node.childNodes.length) node.parentNode.insertBefore(node.firstChild, node);
      node.parentNode.removeChild(node);
    });

    // Remove all applied injections
    nodesToRevert.forEach((node) => {
      this.removeCustomInjections(node);
      node.removeAttribute(this.referenceMap.options.referenceAttribute);
    });

    // Remove all dynamic nodes created by page scripts
    removeSetOfNodes(nodesToRemove);

    return transformedElement;
  }

  synchronizeElement(liveElement) {
    const updateReference = true;
    const transformedElement = this.transformToStatic(liveElement, updateReference);

    const referenceId = this.referenceMap.getReferenceId(liveElement);
    const oldStaticElement = this.referenceMap.getReference(liveElement);
    const elementBeforeTransofrmation = oldStaticElement.cloneNode(true);
    this.options.onBeforeSynchronizeStaticElement(oldStaticElement, transformedElement);
    this.options.onBeforeSynchronizeLiveElement(liveElement);
    oldStaticElement.parentNode.replaceChild(transformedElement, oldStaticElement);
    this.referenceMap.map[referenceId] = transformedElement;
    this.options.onAfterSynchronizeStaticElement(elementBeforeTransofrmation, transformedElement);
    this.options.onAfterSynchronizeLiveElement(liveElement);
  }
}

export default MutationDriver;

export const constants = {
  STATIC_TEXT_TOKEN,
};
